import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Checkbox, Label, Modal, Table, TextInput } from 'flowbite-react';
import { Dispatch, FormEvent, PropsWithChildren, useState } from 'react';
import { InlineWidget } from 'react-calendly';
import { HiInformationCircle } from 'react-icons/hi';
import { Link, useLoaderData, useOutletContext, useParams } from 'react-router-dom';

import { LoaderData, loaders } from 'src/api/loaders';
import { postCreateUseCasesFromOnboarding } from 'src/api/methods.ts';
import type { OnboardingState, Store, UseCase, UserProfile } from 'src/api/models';
import { queries } from 'src/api/queries';
import AbandonedCartIcon from 'src/assets/img/abandonedcart-icon.svg?react';
import CustomIcon from 'src/assets/img/custom-usecase-icon.svg?react';
import ThankYouIcon from 'src/assets/img/thank-you-icon.svg?react';
import WelcomeIcon from 'src/assets/img/welcome-icon.svg?react';
import WinBackIcon from 'src/assets/img/winback-icon.svg?react';
import { LoadingSpinner } from 'src/components/loading-spinner.tsx';
import { DashboardContext } from 'src/routes/dashboard/container.tsx';
import { DashboardInstructions } from 'src/routes/dashboard/instructions';

function onboardingStateToDisplayString(state: OnboardingState): string {
  if (state === 'NOT_STARTED') {
    return 'Not started';
  } else if (state === 'VIDEOS_RECEIVED') {
    return 'Videos received';
  } else if (state === 'VIDEOS_GREENLIT') {
    return 'Videos greenlit';
  } else if (state === 'LIVE') {
    return 'Live';
  } else if (state === 'STOPPED') {
    return 'Stopped';
  } else {
    const result: never = state;
    return result;
  }
}

function stateToActionString(state: OnboardingState): string {
  switch (state) {
    case 'LIVE':
      return 'View';
    case 'STOPPED':
      return 'View';
    case 'VIDEOS_RECEIVED':
      return 'Videos Received';
    case 'VIDEOS_GREENLIT':
      return 'Finish Integration';
    default:
      return 'Upload Videos';
  }
}

export type UseCaseOnboardingCheckboxOptionProps = PropsWithChildren<{
  text: string;
  checkboxID: 'thankyou' | 'abandonedcart' | 'welcome' | 'winback' | 'custom';
  isChecked: boolean;
  setIsChecked: Dispatch<React.SetStateAction<boolean>>;
  customUseCases?: string | undefined;
  setCustomUseCases?: Dispatch<React.SetStateAction<string>> | undefined;
}>;

function UseCaseOnboardingCheckboxOption({
  text,
  checkboxID,
  isChecked,
  setIsChecked,
  customUseCases,
  setCustomUseCases,
}: UseCaseOnboardingCheckboxOptionProps) {
  const iconClassName =
    'w-10 h-10 mr-4 text-blue-600 transition duration-75 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white';
  return (
    <div className='mb-8 flex items-center'>
      {checkboxID === 'thankyou' && <ThankYouIcon className={iconClassName} />}
      {checkboxID === 'abandonedcart' && <AbandonedCartIcon className={iconClassName} />}
      {checkboxID === 'welcome' && <WelcomeIcon className={iconClassName} />}
      {checkboxID === 'winback' && <WinBackIcon className={iconClassName} />}
      {checkboxID === 'custom' && <CustomIcon className={iconClassName} />}
      <Checkbox
        id={checkboxID}
        checked={isChecked}
        onChange={(e) => setIsChecked(e.target.checked)}
        className='h-8 w-8 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600'
      />
      <Label
        htmlFor={checkboxID}
        className='ml-4 text-lg font-medium text-gray-900 dark:text-gray-300'
      >
        {text}
      </Label>
      {setCustomUseCases && checkboxID === 'custom' && (
        <TextInput
          id='custom-use-cases'
          value={customUseCases}
          onChange={(e) => setCustomUseCases(e.target.value)}
          placeholder='Enter the name(s) of your flow(s) here separated by commas'
          className='ml-4 block w-2/4 rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500'
        />
      )}
    </div>
  );
}

function DashboardUseCaseTableRow({
  useCase,
  store,
  userProfile,
  setOpenModal,
}: {
  useCase: UseCase;
  store: Store;
  userProfile: UserProfile | undefined;
  setOpenModal: Dispatch<React.SetStateAction<boolean>>;
}) {
  const { isLoading: metricsAreLoading, data: metrics } = useQuery({
    ...queries.storeAggregatedMetrics(store.key_name, 30, useCase.id),
    placeholderData: keepPreviousData,
  });
  return (
    <Table.Row key={useCase.id}>
      <Table.Cell>{useCase.pretty_name ?? useCase.name}</Table.Cell>
      {store.show_attribution_metrics && (
        <Table.Cell className='text-right'>
          {metricsAreLoading && <LoadingSpinner size='lg' />}
          {metricsAreLoading ||
            (useCase.show_attribution_metrics &&
              Math.round((metrics?.centsDriven ?? 0) / 100).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
                useGrouping: true,
              }))}
          {metricsAreLoading || (!useCase.show_attribution_metrics && 'N/A')}
        </Table.Cell>
      )}
      <Table.Cell className='text-right'>
        {metricsAreLoading && <LoadingSpinner size='lg' />}
        {metricsAreLoading ||
          (metrics?.customerResponses ?? 0).toLocaleString('en', {
            useGrouping: true,
          })}
      </Table.Cell>
      <Table.Cell className='text-right'>
        {metricsAreLoading && <LoadingSpinner size='lg' />}
        {metricsAreLoading ||
          (metrics?.sentMessages ?? 0).toLocaleString('en', {
            useGrouping: true,
          })}
      </Table.Cell>
      <Table.Cell className='text-left'>
        {onboardingStateToDisplayString(useCase.onboarding_state)}
      </Table.Cell>
      <Table.Cell className='float-right flex flex-row'>
        {(!userProfile || (userProfile && userProfile.state !== 'NEW_USER')) && (
          <>
            {useCase.onboarding_state === 'NOT_STARTED' && (
              <Link to={`${useCase.id}/generate-script`} className='mr-4'>
                <Button className='w-48' gradientDuoTone='redToYellow' outline={true}>
                  Generate script with AI
                </Button>
              </Link>
            )}

            <Link to={`${useCase.id}`}>
              <Button
                className='w-40'
                gradientDuoTone={
                  ['LIVE', 'VIDEOS_RECEIVED'].includes(useCase.onboarding_state)
                    ? 'purpleToBlue'
                    : 'redToYellow'
                }
                outline={['LIVE', 'VIDEOS_RECEIVED'].includes(useCase.onboarding_state)}
              >
                {stateToActionString(useCase.onboarding_state)}
              </Button>
            </Link>
          </>
        )}
        {userProfile && userProfile.state === 'NEW_USER' && (
          <Button
            className='w-40'
            gradientDuoTone={
              ['LIVE', 'VIDEOS_RECEIVED'].includes(useCase.onboarding_state)
                ? 'purpleToBlue'
                : 'redToYellow'
            }
            outline={['LIVE', 'VIDEOS_RECEIVED'].includes(useCase.onboarding_state)}
            onClick={() => setOpenModal(true)}
          >
            {stateToActionString(useCase.onboarding_state)}
          </Button>
        )}
      </Table.Cell>
    </Table.Row>
  );
}

function DashboardUseCaseTable({
  usecases,
  store,
  userProfile,
  setOpenModal,
}: {
  usecases: UseCase[];
  store: Store;
  userProfile: UserProfile | undefined;
  setOpenModal: Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <Table striped>
      <Table.Head>
        <Table.HeadCell>Name</Table.HeadCell>
        {store.show_attribution_metrics && (
          <Table.HeadCell className='text-right'>Revenue From Last 30 Days</Table.HeadCell>
        )}
        <Table.HeadCell className='text-right'>Responses From Last 30 Days</Table.HeadCell>
        <Table.HeadCell className='text-right'>Sent Messages From Last 30 Days</Table.HeadCell>
        <Table.HeadCell className='text-left'>Status</Table.HeadCell>
        <Table.HeadCell>
          <span className='sr-only'>Action</span>
        </Table.HeadCell>
      </Table.Head>
      <Table.Body className='divide-y'>
        {usecases.map((u) => (
          <DashboardUseCaseTableRow
            useCase={u}
            store={store}
            key={u.id}
            userProfile={userProfile}
            setOpenModal={setOpenModal}
          />
        ))}
      </Table.Body>
    </Table>
  );
}

export function DashboardVideoFlowList() {
  const { store, userProfile } = useOutletContext<DashboardContext>();
  const initialData = useLoaderData() as LoaderData<typeof loaders.usecasesForStoreLoader>;
  const { storeKey } = useParams();

  const { data: usecases } = useQuery({
    ...queries.usecasesForStore(storeKey),
    initialData: initialData,
    enabled: !!storeKey,
  });

  const [openModal, setOpenModal] = useState(false);
  const [isThankYouChecked, setIsThankYouChecked] = useState(false);
  const [isAbandonedCartChecked, setIsAbandonedCartChecked] = useState(false);
  const [isWelcomeChecked, setIsWelcomeChecked] = useState(false);
  const [isWinBackChecked, setIsWinBackChecked] = useState(false);
  const [isCustomChecked, setIsCustomChecked] = useState(false);
  const [customUseCases, setCustomUseCases] = useState('');
  const [isInitialUseCaseError, setIsInitialUseCaseError] = useState(false);

  const saveInitialUseCases = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let allUseCases = [];
    if (isThankYouChecked) {
      allUseCases.push('thankyou');
    }
    if (isAbandonedCartChecked) {
      allUseCases.push('abandonedcart');
    }
    if (isWinBackChecked) {
      allUseCases.push('winback');
    }
    if (isWelcomeChecked) {
      allUseCases.push('welcome');
    }
    if (isCustomChecked && customUseCases !== '') {
      allUseCases = allUseCases.concat(customUseCases.split(','));
    }

    if (store) {
      postCreateUseCasesFromOnboarding(store.key_name, JSON.stringify(allUseCases))
        .then((resData) => {
          if (resData?.data) {
            window.location.assign('/app/' + store.key_name + '/video-flows/');
          }
        })
        .catch((res) => {
          console.log(res);
          setIsInitialUseCaseError(true);
        });
    }
  };

  if (!store) {
    return null;
  }

  if (usecases.results.length === 0) {
    return (
      <Card>
        {isInitialUseCaseError && (
          <Alert color='failure' icon={HiInformationCircle}>
            <span>
              <p>
                <span className='font-medium'>Save failed!</span> Verify there aren&apos;t any
                mistakes, and try again.
              </p>
            </span>
          </Alert>
        )}
        <div className='mb-12 flex w-full'>
          <p className='text-md font-medium text-gray-900 dark:text-gray-300'>
            Select the flows you&apos;ll be recording videos for
          </p>
        </div>
        <form onSubmit={saveInitialUseCases}>
          <UseCaseOnboardingCheckboxOption
            text='Post-purchase Thank You'
            checkboxID='thankyou'
            isChecked={isThankYouChecked}
            setIsChecked={setIsThankYouChecked}
          />
          <UseCaseOnboardingCheckboxOption
            text='Abandoned Checkout'
            checkboxID='abandonedcart'
            isChecked={isAbandonedCartChecked}
            setIsChecked={setIsAbandonedCartChecked}
          />
          <UseCaseOnboardingCheckboxOption
            text='Newsletter Signup'
            checkboxID='welcome'
            isChecked={isWelcomeChecked}
            setIsChecked={setIsWelcomeChecked}
          />
          <UseCaseOnboardingCheckboxOption
            text='Winback'
            checkboxID='winback'
            isChecked={isWinBackChecked}
            setIsChecked={setIsWinBackChecked}
          />
          <UseCaseOnboardingCheckboxOption
            text='Custom'
            checkboxID='custom'
            isChecked={isCustomChecked}
            setIsChecked={setIsCustomChecked}
            customUseCases={customUseCases}
            setCustomUseCases={setCustomUseCases}
          />
          <div className='flow-root'>
            <Button
              type='submit'
              disabled={
                !(
                  isThankYouChecked ||
                  isAbandonedCartChecked ||
                  isWelcomeChecked ||
                  isWinBackChecked ||
                  (isCustomChecked && customUseCases !== '')
                )
              }
              className='float-right mb-2 mr-2 rounded-lg bg-gradient-to-br from-purple-600 to-blue-500 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-gradient-to-bl focus:outline-none focus:ring-4 focus:ring-blue-300'
            >
              Save
            </Button>
          </div>
        </form>
      </Card>
    );
  }

  return (
    <div className='flex w-full flex-col gap-6'>
      <Card>
        <DashboardUseCaseTable
          usecases={usecases.results}
          store={store}
          userProfile={userProfile}
          setOpenModal={setOpenModal}
        />
        <Button color='dark' className='w-44' onClick={() => setOpenModal(true)}>
          Add New Flow
        </Button>
        <Modal dismissible show={openModal} onClose={() => setOpenModal(false)}>
          <Modal.Header>
            {userProfile && userProfile.state === 'NEW_USER' ? 'Schedule A Call!' : 'Contact Us!'}
          </Modal.Header>
          <Modal.Body>
            {userProfile && userProfile.state === 'NEW_USER' && (
              <div className='App'>
                <InlineWidget url='https://calendly.com/d/5f8-nrk-xrj' />
              </div>
            )}
            {(!userProfile || (userProfile && userProfile.state !== 'NEW_USER')) && (
              <div className='space-y-6'>
                <p className='text-base leading-relaxed text-gray-500 dark:text-gray-400'>
                  We&apos;re happy to help add a new flow to your account. Reach out to{' '}
                  <a href='mailto:team@themavericklab.com'>team@themavericklab.com</a> to get
                  started.
                </p>
              </div>
            )}
          </Modal.Body>
          {(!userProfile || (userProfile && userProfile.state !== 'NEW_USER')) && (
            <Modal.Footer>
              <Button onClick={() => setOpenModal(false)}>OK</Button>
            </Modal.Footer>
          )}
        </Modal>
      </Card>
      <DashboardInstructions
        setOpenModal={userProfile && userProfile.state === 'NEW_USER' ? setOpenModal : undefined}
      />
    </div>
  );
}
